import classNames from "classnames";
import React, { FC, useState } from "react";
import { HiPlus } from "react-icons/hi2";
import { LuLock } from "react-icons/lu";
import { useSearchParams } from "react-router-dom";

import { Card } from "../../components/card/Card";
import { useGetClientPortalQuery } from "../../slices/ClientPortalSlice";
import { useGetClientFilesQuery } from "../../slices/DocumentsSlice";
import { useGetClientSignaturesQuery } from "../../slices/SignaturesSlice";
import { SignaturesSection } from "../signatures/SignaturesSection";
import {
  portalCardPeople,
  portalSummary,
  portalTabFiles,
} from "./clientPortal.utils";
import { InviteToPortalModal } from "./InviteToPortalModal";
import { PortalFilesSection } from "./PortalFilesSection";
import { PortalPeopleSection } from "./PortalPeopleSection";

type PortalTab = "files" | "signatures" | "people";

const PORTAL_TABS: PortalTab[] = ["files", "signatures", "people"];

/**
 * The client page's "Client portal" tab (Roadmap 1880, owner 2026-10-22: "the
 * client portal should just have its own tab ... a whole page for the client
 * portal"). Before, it was a widget on the profile (Roadmap 1876 / 1879).
 * Inner tabs (owner's order "files, signature, people"): Files (what the client
 * sees in the portal: their uploads + shared files), Signatures (e-signature
 * requests, Request signature) and People (portal access, Invite). Files is
 * open first; the picked inner tab is kept in the address (?view=) so Back and
 * reload keep it. Full width, no fixed height: lists use the page's room.
 * "Invite to portal" sits in the card's header so it shows on every inner tab
 * (owner 2026-10-28, Loom 51918aea: "no matter which tab we're on, we always
 * see the invite button"); the People tab no longer has its own.
 */
export const ClientPortalTab: FC<{
  customerId: string;
  organizationId?: string;
}> = ({ customerId, organizationId }) => {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") as PortalTab | null;
  const tab: PortalTab = view && PORTAL_TABS.includes(view) ? view : "files";
  const setTab = (next: PortalTab) => {
    const p = new URLSearchParams(params);
    if (next === "files") p.delete("view");
    else p.set("view", next);
    setParams(p, { replace: true });
  };
  // Same queries as the sections (RTK Query shares them): counts for the tabs.
  const { data: portal } = useGetClientPortalQuery(
    { customerId },
    { skip: !customerId },
  );
  const { data: clientFiles } = useGetClientFilesQuery(customerId, {
    skip: !customerId,
  });
  const { data: signatures } = useGetClientSignaturesQuery(customerId, {
    skip: !customerId,
  });
  const [inviteOpen, setInviteOpen] = useState(false);
  const archived = !!portal?.archived;
  const people = portalCardPeople(portal?.people ?? []).filter(
    (p) => p.status !== "revoked",
  );
  const waiting = (signatures ?? []).filter((r) => r.status === "sent").length;
  const portalFiles = portalTabFiles(clientFiles?.files ?? []).length;
  const tabs: { key: PortalTab; label: string; count?: number }[] = [
    { key: "files", label: "Files", count: portalFiles },
    { key: "signatures", label: "Signatures", count: waiting },
    { key: "people", label: "People", count: people.length },
  ];

  return (
    <>
      <Card extraClasses={"overflow-hidden"}>
        <div
          data-testid={"client-portal-tab"}
          className={"flex min-h-[420px] min-w-0 flex-col text-[#1F2433]"}>
          <div className={"flex shrink-0 items-start gap-3.5 px-6 pt-6"}>
            <span
              className={
                "flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-[11px] bg-[#EFEAFE] text-[#7C5CFC]"
              }>
              <LuLock size={18} />
            </span>
            <div className={"min-w-0 flex-1"}>
              <div className={"flex flex-wrap items-center gap-2.5"}>
                <span className={"text-[20px] font-extrabold"}>
                  Client portal
                </span>
                {portal && <PortalStateBadge state={portalSummary(people)} />}
              </div>
              <p className={"mt-1 text-[13px] font-medium text-[#6B7185]"}>
                Files you share, files the client uploads, documents to sign,
                and who can sign in.
              </p>
            </div>
            <button
              type={"button"}
              data-testid={"portal-invite-button"}
              onClick={() => setInviteOpen(true)}
              disabled={!portal || archived}
              title={
                archived
                  ? "This client is archived. Unarchive it to invite people."
                  : "Invite this client's contacts to sign in to their portal"
              }
              className={
                "inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-[9px] bg-[#6B4EFF] pl-3.5 pr-4 text-[14px] font-bold leading-none text-white shadow-[0_3px_10px_rgba(107,78,255,0.3)] hover:bg-[#5B3DF5] disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
              }>
              <HiPlus size={16} className={"shrink-0"} /> Invite
            </button>
          </div>

          <div
            role={"tablist"}
            className={
              "mt-4 flex shrink-0 gap-7 border-b border-[#EDEFF3] px-6"
            }>
            {tabs.map((t) => (
              <button
                key={t.key}
                type={"button"}
                role={"tab"}
                aria-selected={tab === t.key}
                onClick={() => setTab(t.key)}
                className={classNames(
                  "-mb-px inline-flex items-center gap-2 border-b-2 pb-2.5 text-[14px] font-semibold",
                  tab === t.key
                    ? "border-[#7C3AED] text-[#7C3AED]"
                    : "border-transparent text-[#3A4256] hover:text-[#1F2433]",
                )}>
                {t.label}
                <span
                  className={classNames(
                    "min-w-[20px] rounded-full px-1.5 text-center text-[11px] font-bold leading-[20px]",
                    tab === t.key
                      ? "bg-[#7C3AED] text-white"
                      : "bg-[#EDEFF3] text-[#6B7185]",
                  )}>
                  {t.count ?? 0}
                </span>
              </button>
            ))}
          </div>

          <div role={"tabpanel"} className={"min-w-0 flex-1 px-6 py-5"}>
            {tab === "files" ? (
              <PortalFilesSection customerId={customerId} />
            ) : tab === "signatures" ? (
              <SignaturesSection
                customerId={customerId}
                organizationId={organizationId}
              />
            ) : (
              <PortalPeopleSection
                customerId={customerId}
                onInvite={
                  portal && !archived ? () => setInviteOpen(true) : undefined
                }
              />
            )}
          </div>
        </div>
      </Card>
      {inviteOpen && portal && !archived && (
        <InviteToPortalModal
          overview={portal}
          onClose={() => setInviteOpen(false)}
        />
      )}
    </>
  );
};

/** "No access yet" / "Invite sent" / "Active" next to the title (owner's design 2026-10-28). */
const PortalStateBadge: FC<{ state: "active" | "invited" | "none" }> = ({
  state,
}) => {
  const look = {
    active: {
      box: "border-transparent bg-[#E9F9F1] text-[#147A4B]",
      dot: "bg-[#22A06B]",
      label: "Active",
    },
    invited: {
      box: "border-transparent bg-[#FFF4DB] text-[#8A5A00]",
      dot: "bg-[#D99A00]",
      label: "Invite sent",
    },
    none: {
      box: "border-[#DCE2EA] bg-white text-[#3A4256]",
      dot: "bg-[#B8BECB]",
      label: "No access yet",
    },
  }[state];
  return (
    <span
      data-testid={"portal-state-badge"}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11.5px] font-bold ${look.box}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${look.dot}`} />
      {look.label}
    </span>
  );
};

/** Route element for the client page's "Client portal" tab (both client types). */
export const ClientPortalRoute: FC<{
  customerId?: string | null;
  organizationId?: string;
}> = ({ customerId, organizationId }) =>
  customerId ? (
    <ClientPortalTab customerId={customerId} organizationId={organizationId} />
  ) : null;
